import type { Key } from 'react';

import {
  type CalendarDateTime,
  parseDateTime,
  Time,
  toCalendarDateTime,
} from '@internationalized/date';
import type { DateValue } from 'react-aria-components/Calendar';

import type { TimeFormatType } from '../components/time_picker/time_picker';

export const DEFAULT_TIME = new Time(12, 0, 0);
export const TIME_FORMAT_STORAGE_KEY = 'uad_datetime_field_time_format';
export const LEGACY_TIME_FORMAT_STORAGE_KEY = 'timePickerTimeFormat';

/**
 * Value the calendar and time inputs fall back to while nothing is selected.
 * `CalendarDateTime` is immutable, so a single shared instance is safe.
 */
export const PLACEHOLDER_DATETIME = parseDateTime('2026-01-01T00:00:00');

export type TimeInputs = {
  hours: string;
  minutes: string;
  seconds: string;
};

export function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

export function formatHourForDisplay(
  hourIn24Format: number,
  timeFormat: TimeFormatType,
): string {
  if (timeFormat === '24h') {
    return pad2(hourIn24Format);
  }

  const hourIn12Format = hourIn24Format % 12;
  if (hourIn12Format === 0) {
    return '12';
  }

  return pad2(hourIn12Format);
}

export function to24HourFromDisplay(
  displayHour: string,
  timeFormat: TimeFormatType,
): number {
  const parsedHour = Number.parseInt(displayHour, 10);
  if (Number.isNaN(parsedHour)) {
    return 0;
  }

  if (timeFormat === '24h') {
    return Math.min(Math.max(parsedHour, 0), 23);
  }

  if (parsedHour === 12) {
    return timeFormat === 'pm' ? 12 : 0;
  }

  return timeFormat === 'pm' ? parsedHour + 12 : parsedHour;
}

export function ensureDateTime(
  value: DateValue | null | undefined,
  fallbackTime: Time,
): CalendarDateTime | null {
  if (!value) {
    return null;
  }
  if ('hour' in value) {
    return toCalendarDateTime(value);
  }
  return toCalendarDateTime(value, fallbackTime);
}

export function getTimeInputsFromDatetime(
  value: CalendarDateTime,
  timeFormat: TimeFormatType,
): TimeInputs {
  return {
    hours: formatHourForDisplay(value.hour, timeFormat),
    minutes: pad2(value.minute),
    seconds: pad2(value.second ?? 0),
  };
}

/**
 * The meridiem used to open a 12-hour picker must come from the value, not a
 * remembered preference, so an existing PM time is not silently read as AM.
 */
export function resolveOpenTimeFormat(
  appliedFormat: TimeFormatType,
  value: CalendarDateTime,
): TimeFormatType {
  if (appliedFormat === '24h') {
    return '24h';
  }
  return value.hour >= 12 ? 'pm' : 'am';
}

export function createTimeFromInputs(
  { hours, minutes, seconds }: TimeInputs,
  timeFormat: TimeFormatType,
): Time {
  return new Time(
    to24HourFromDisplay(hours, timeFormat),
    Number.parseInt(minutes, 10),
    Number.parseInt(seconds, 10),
  );
}

/**
 * Resolves the next hours input when the time picker clears its selection,
 * which happens while the user is typing into the combo box.
 */
export function resolveHoursInput(
  key: Key | null,
  timeFormat: TimeFormatType,
  currentHours: string,
): string {
  if (key !== null) {
    return String(key);
  }

  return timeFormat === '24h' ? '00' : currentHours || '12';
}

export function applyTimeToDatetime(
  value: CalendarDateTime,
  time: Time,
): CalendarDateTime {
  return value.set({
    hour: time.hour,
    minute: time.minute,
    second: time.second,
  });
}
